//
// src/api.js
//
// Central Axios client for FastAPIPOS.
//
// Responsibilities:
// - Resolve the backend API origin
// - Attach authentication credentials
// - Attach the active tenant workspace
// - Normalize relative API paths
// - Support JSON and multipart/form-data safely
// - Keep authentication and licensing errors separate
// - Preserve customer access when a licence requires renewal
// - Redirect only on genuine authentication-session failure
//

import axios from "axios";


// =====================================================
// API Configuration
// =====================================================

const RAW_API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:8001";


const BASE_DOMAIN =
  String(RAW_API_URL)
    .trim()
    .replace(/\/+$/, "");


if (
  process.env.NODE_ENV === "production" &&
  !process.env.REACT_APP_API_URL
) {
  console.error(
    "REACT_APP_API_URL is not configured for the production build."
  );
}


const API = axios.create({
  baseURL: `${BASE_DOMAIN}/api`,
  timeout: 30000,
});


// =====================================================
// Storage Helpers
// =====================================================

function getStoredUser() {
  const rawUser =
    localStorage.getItem("user");

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch (error) {
    console.warn(
      "Stored user information could not be read:",
      error
    );

    return null;
  }
}


function clearAuthenticationStorage() {
  localStorage.removeItem("token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  localStorage.removeItem("tenant_id");
  localStorage.removeItem("tenant_tier");

  sessionStorage.removeItem("token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("tenant_id");
  sessionStorage.removeItem("tenant_tier");
}


// =====================================================
// Tenant Resolution
// =====================================================

function normalizeTenantId(value) {
  if (!value) {
    return null;
  }

  const normalized =
    String(value)
      .trim()
      .toLowerCase();

  return normalized || null;
}


function getActiveTenant() {
  const storedTenant =
    normalizeTenantId(
      localStorage.getItem("tenant_id")
    );

  if (storedTenant) {
    return storedTenant;
  }

  const storedUser =
    getStoredUser();

  const userTenant =
    normalizeTenantId(
      storedUser?.tenant_id
    );

  if (userTenant) {
    return userTenant;
  }

  // Do not invent a tenant such as "default".
  return null;
}


// =====================================================
// Authentication Token
// =====================================================

function getAccessToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    null
  );
}


// =====================================================
// URL Helpers
// =====================================================

function normalizeRequestPath(requestUrl) {
  if (!requestUrl) {
    return requestUrl;
  }

  const url =
    String(requestUrl).trim();

  if (
    /^https?:\/\//i.test(url)
  ) {
    return url;
  }

  const [
    rawPath,
    ...queryParts
  ] = url.split("?");

  const queryString =
    queryParts.join("?");

  let cleanPath =
    String(rawPath || "")
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");

  if (cleanPath === "api") {
    cleanPath = "";
  } else if (
    cleanPath.startsWith("api/")
  ) {
    cleanPath =
      cleanPath.slice(4);
  }

  const normalizedPath =
    cleanPath
      ? `/${cleanPath}`
      : "/";

  return (
    normalizedPath +
    (
      queryString
        ? `?${queryString}`
        : ""
    )
  );
}


// =====================================================
// Request Classification
// =====================================================

function getRequestPath(config) {
  const requestUrl =
    config?.url || "";

  try {
    return new URL(
      requestUrl,
      `${BASE_DOMAIN}/api/`
    ).pathname.toLowerCase();
  } catch {
    return String(
      requestUrl
    ).toLowerCase();
  }
}


function isLoginEndpoint(config) {
  const path =
    getRequestPath(config);

  return (
    path.endsWith("/api/login") ||
    path.endsWith("/login")
  );
}


function isPublicEndpoint(config) {
  const path =
    getRequestPath(config);

  return (
    path.endsWith("/api/login") ||
    path.endsWith("/login") ||
    path.includes("/tenants/register")
  );
}


function isLicensingEndpoint(config) {
  const path =
    getRequestPath(config);

  return (
    path.includes("/billing/") ||
    path.endsWith("/billing") ||
    path.includes("/licensing/") ||
    path.includes("/licenses/")
  );
}


// =====================================================
// Header Helpers
// =====================================================

function hasHeader(
  headers,
  headerName
) {
  if (!headers) {
    return false;
  }

  if (
    typeof headers.has === "function"
  ) {
    return headers.has(
      headerName
    );
  }

  return Boolean(
    headers[headerName] ||
    headers[
      headerName.toLowerCase()
    ]
  );
}


function setHeader(
  headers,
  headerName,
  value
) {
  if (
    headers &&
    typeof headers.set === "function"
  ) {
    headers.set(
      headerName,
      value
    );

    return;
  }

  if (headers) {
    headers[headerName] =
      value;
  }
}


function deleteHeader(
  headers,
  headerName
) {
  if (!headers) {
    return;
  }

  if (
    typeof headers.delete === "function"
  ) {
    headers.delete(
      headerName
    );

    return;
  }

  delete headers[
    headerName
  ];

  delete headers[
    headerName.toLowerCase()
  ];
}


// =====================================================
// Request Interceptor
// =====================================================

API.interceptors.request.use(
  (config) => {
    config.headers =
      config.headers || {};


    // -------------------------------------------------
    // Authentication
    // -------------------------------------------------

    const token =
      getAccessToken();

    if (
      token &&
      !hasHeader(
        config.headers,
        "Authorization"
      )
    ) {
      setHeader(
        config.headers,
        "Authorization",
        `Bearer ${token}`
      );
    }


    // -------------------------------------------------
    // Tenant Workspace
    // -------------------------------------------------

    const activeTenant =
      getActiveTenant();

    if (
      activeTenant &&
      !hasHeader(
        config.headers,
        "X-Tenant-ID"
      )
    ) {
      setHeader(
        config.headers,
        "X-Tenant-ID",
        activeTenant
      );
    }


    // -------------------------------------------------
    // Content-Type
    // -------------------------------------------------
    //
    // For FormData:
    // let Axios/browser generate:
    //
    // multipart/form-data; boundary=...
    //
    // For normal objects:
    // Axios will serialize JSON automatically.
    // -------------------------------------------------

    if (
      typeof FormData !== "undefined" &&
      config.data instanceof FormData
    ) {
      deleteHeader(
        config.headers,
        "Content-Type"
      );
    }


    // -------------------------------------------------
    // URL Normalization
    // -------------------------------------------------

    config.url =
      normalizeRequestPath(
        config.url
      );

    return config;
  },

  (error) =>
    Promise.reject(error)
);


// =====================================================
// Response Interceptor
// =====================================================

API.interceptors.response.use(
  (response) =>
    response,

  (error) => {
    const statusCode =
      error?.response?.status;

    const requestConfig =
      error?.config || {};

    const loginRequest =
      isLoginEndpoint(
        requestConfig
      );

    const publicRequest =
      isPublicEndpoint(
        requestConfig
      );

    const licensingRequest =
      isLicensingEndpoint(
        requestConfig
      );


    // =================================================
    // HTTP 402 - Subscription / Payment Required
    // =================================================
    //
    // A licence/subscription issue must never log the
    // customer out.
    // =================================================

    if (
      statusCode === 402
    ) {
      const detail =
        error?.response
          ?.data
          ?.detail;

      const message =
        typeof detail === "string"
          ? detail
          : (
              detail?.message ||
              (
                "Your subscription requires renewal. " +
                "You can continue using the workspace and " +
                "renew from the Billing page."
              )
            );

      console.info(
        "Subscription renewal required:",
        message
      );

      window.dispatchEvent(
        new CustomEvent(
          "subscriptionRequired",
          {
            detail: {
              status:
                "renewal_required",

              renewal_required:
                true,

              can_create_sales:
                false,

              message,

              response:
                error?.response
                  ?.data ||
                null,
            },
          }
        )
      );

      return Promise.reject(
        error
      );
    }


    // =================================================
    // HTTP 403 - Authenticated but Not Permitted
    // =================================================

    if (
      statusCode === 403
    ) {
      console.warn(
        "The current account is not permitted to perform this operation."
      );

      return Promise.reject(
        error
      );
    }


    // =================================================
    // HTTP 401 - Authentication Failure
    // =================================================

    if (
      statusCode === 401 &&
      !loginRequest &&
      !publicRequest
    ) {
      const currentToken =
        getAccessToken();

      const authorizationHeader =
        requestConfig
          ?.headers
          ?.Authorization;


      // ------------------------------------------------
      // Licensing Compatibility Safeguard
      // ------------------------------------------------

      if (licensingRequest) {
        console.warn(
          "A licensing endpoint returned HTTP 401. " +
          "The customer session has been preserved."
        );

        return Promise.reject(
          error
        );
      }


      // ------------------------------------------------
      // Genuine Authenticated Request Rejected
      // ------------------------------------------------

      if (
        currentToken &&
        authorizationHeader
      ) {
        console.warn(
          "Your session is no longer valid. Please sign in again."
        );

        clearAuthenticationStorage();

        window.dispatchEvent(
          new CustomEvent(
            "authenticationExpired"
          )
        );

        if (
          window.location.pathname !==
          "/login"
        ) {
          window.location.replace(
            "/login"
          );
        }
      }
    }


    // =================================================
    // Network / Server Diagnostics
    // =================================================

    if (
      !error.response
    ) {
      console.error(
        "The API could not be reached:",
        error.message
      );

    } else if (
      statusCode >= 500
    ) {
      console.error(
        "The server could not complete the request:",
        {
          status:
            statusCode,

          url:
            requestConfig.url,

          detail:
            error.response
              ?.data
              ?.detail,
        }
      );
    }

    return Promise.reject(
      error
    );
  }
);


export default API;